import { initialLaborIntakeData } from './laborIntakeSchema';

const DRAFT_STORAGE_KEY = 'wazin_labor_intake_draft_v2';
const LOCAL_HOSTS = ['localhost', '127.0.0.1'];
const isLocalDevelopment =
  typeof window !== 'undefined' && LOCAL_HOSTS.includes(window.location.hostname);
const API_URL = isLocalDevelopment ? 'http://localhost:8001' : (process.env.REACT_APP_BACKEND_URL || '');

const clone = (value) => JSON.parse(JSON.stringify(value));

const createAttachmentId = (name, size) =>
  `${name}-${size}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const createDraftReference = () => {
  const year = new Date().getFullYear();
  const suffix = Math.floor(1000 + Math.random() * 9000);
  return `WZN-DRAFT-${year}-${suffix}`;
};

const sanitizeFiles = (files) =>
  Array.from(files || []).map((file) => ({
    id: createAttachmentId(file.name, file.size),
    name: file.name,
    size: file.size,
    type: file.type || 'application/octet-stream',
    uploadedAt: new Date().toISOString(),
  }));

const sanitizeAttachmentItems = (items) =>
  Array.from(items || []).map((item) => ({
    id: item.id || createAttachmentId(item.fileName || item.name || 'attachment', item.size || 0),
    category: item.category || '',
    typeKey: item.typeKey || '',
    typeLabel: item.typeLabel || item.displayName || item.name || '',
    typeLabelEn: item.typeLabelEn || '',
    displayName: item.displayName || item.typeLabel || item.name || '',
    fileName: item.fileName || item.name || '',
    name: item.fileName || item.name || item.displayName || '',
    storageKey: item.storageKey || item.storage_key || '',
    fileId: item.fileId || item.file_id || '',
    fileUrl: item.fileUrl || item.url || item.documentUrl || item.downloadUrl || '',
    extension: item.extension || '',
    isPreviewable: Boolean(item.isPreviewable),
    size: item.size || 0,
    type: item.mimeType || item.type || 'application/octet-stream',
    mimeType: item.mimeType || item.type || 'application/octet-stream',
    uploadedAt: item.uploadedAt || new Date().toISOString(),
    status: item.status || 'saved',
  }));

export const uploadLaborAttachments = async (files) => {
  const formData = new FormData();
  Array.from(files || []).forEach((file) => formData.append('files', file));
  const response = await fetch(`${API_URL}/api/labor-intake/attachments/upload`, {
    method: 'POST',
    body: formData,
  });
  const data = await readJson(response);
  return data.files || [];
};

const toAmount = (value) => Number(String(value ?? '').replace(/,/g, '')) || 0;

const inclusiveDays = (fromDate, toDate) => {
  if (!fromDate || !toDate) return 0;
  const start = new Date(fromDate);
  const end = new Date(toDate);
  const difference = Math.floor((end - start) / (24 * 60 * 60 * 1000));
  return difference >= 0 ? difference + 1 : 0;
};

const calculateCompensation = (compensation = {}) => {
  const unpaidWagePeriods = (compensation.unpaidWagePeriods || []).map((period) => {
    const quantity = period.periodType === 'شهر كامل' ? 1 : inclusiveDays(period.fromDate, period.toDate);
    const wage = period.periodType === 'شهر كامل' ? toAmount(period.monthlyWage) : toAmount(period.dailyWage);
    return { ...period, quantity, totalAmount: quantity * wage };
  });
  const leaveBalancePeriods = (compensation.leaveBalancePeriods || []).map((period) => ({
    ...period,
    totalAmount: toAmount(period.days) * toAmount(period.dailyWage),
  }));
  const commissionClaims = compensation.commissionClaims || [];
  const delayedWages = unpaidWagePeriods.reduce((sum, period) => sum + toAmount(period.totalAmount), 0);
  const leaveCompensation = leaveBalancePeriods.reduce((sum, period) => sum + toAmount(period.totalAmount), 0);
  const commissions = commissionClaims.reduce((sum, claim) => sum + toAmount(claim.amount), 0);

  return {
    ...compensation,
    unpaidWagePeriods,
    leaveBalancePeriods,
    commissionClaims,
    financialTotals: {
      delayedWages,
      leaveCompensation,
      commissions,
      approximateTotal: delayedWages + leaveCompensation + commissions,
    },
  };
};

export const getEmptyLaborIntakeData = () => clone(initialLaborIntakeData);

export const saveLaborIntakeDraft = (draft) => {
  const existingDraft = getLaborIntakeDraft();
  const draftPayload = {
    ...draft,
    draftReference: draft.draftReference || existingDraft?.draftReference || createDraftReference(),
    attachments: {
      ...draft.attachments,
      items: sanitizeAttachmentItems(draft.attachments?.items || draft.attachments?.files || []),
    },
  };

  const storedDraft = {
    ...draftPayload,
    _savedAt: new Date().toISOString(),
  };

  localStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify(storedDraft));
  return storedDraft;
};

export const getLaborIntakeDraft = () => {
  try {
    return JSON.parse(localStorage.getItem(DRAFT_STORAGE_KEY) || 'null');
  } catch (error) {
    console.error('Failed to read labor intake draft:', error);
    return null;
  }
};

export const clearLaborIntakeDraft = () => {
  localStorage.removeItem(DRAFT_STORAGE_KEY);
};

const readJson = async (response) => {
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.detail || 'تعذر تنفيذ طلب القضايا العمالية.');
  return data;
};

export const createLaborCase = async (formData) => {
  const payload = {
    ...formData,
    compensation: calculateCompensation(formData.compensation),
    attachments: {
      ...formData.attachments,
      items: sanitizeAttachmentItems(formData.attachments.items || formData.attachments.files || []),
    },
  };
  const response = await fetch(`${API_URL}/api/labor-intake/requests`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const newCase = await readJson(response);
  clearLaborIntakeDraft();
  return newCase;
};

export const getLaborCaseByTracking = async ({ referenceNumber, trackingToken, clientMobile }) => {
  const query = new URLSearchParams({ reference_number: referenceNumber });
  if (trackingToken) query.set('tracking_token', trackingToken);
  if (clientMobile) query.set('client_mobile', clientMobile);
  const response = await fetch(`${API_URL}/api/labor-intake/requests/track?${query.toString()}`);
  if (response.status === 404) return null;
  return readJson(response);
};

export const getLaborCaseByTrackingToken = async (trackingToken) => {
  const response = await fetch(`${API_URL}/api/labor-intake/requests/track/${encodeURIComponent(trackingToken)}`);
  if (response.status === 404) return null;
  return readJson(response);
};

export const addAdditionalDocuments = async ({ caseId, trackingToken, files }) => {
  const uploadedFiles = await uploadLaborAttachments(files);
  const mappedFiles = uploadedFiles.length ? uploadedFiles : sanitizeFiles(files);
  const response = await fetch(`${API_URL}/api/labor-intake/requests/${caseId}/additional-documents`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ tracking_token: trackingToken, documents: mappedFiles }),
  });
  return readJson(response);
};

export const submitLaborCompletion = async ({ caseId, trackingToken, updates, files, attachments }) => {
  const sourceAttachments = attachments || [];
  const filesToUpload = sourceAttachments.length ? sourceAttachments.flatMap((item) => item.files || []) : (files || []);
  const uploadedFiles = filesToUpload.length ? await uploadLaborAttachments(filesToUpload) : [];
  let uploadIndex = 0;
  const mappedDocuments = sourceAttachments.length
    ? sourceAttachments.flatMap((item) => (item.files || []).map(() => {
      const uploaded = uploadedFiles[uploadIndex] || {};
      uploadIndex += 1;
      return {
        ...uploaded,
        typeKey: item.typeKey,
        typeLabel: item.typeLabel,
        displayName: item.displayName || item.typeLabel,
        customName: item.displayName,
        category: 'completion',
      };
    }))
    : (uploadedFiles.length ? uploadedFiles : sanitizeFiles(files || []));
  const response = await fetch(`${API_URL}/api/labor-intake/requests/${caseId}/completion`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      tracking_token: trackingToken,
      updates,
      documents: mappedDocuments,
    }),
  });
  return readJson(response);
};
